import { Component, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject, takeUntil } from 'rxjs';
import {
  ContractRevocationPreviewModel,
  ContractRevocationRequestModel,
} from '../../../models/booking.model';
import { CouponCodeModel } from '../../../models/coupon.model';
import { AlertService } from '../../../services/alert.service';
import { BookingService } from '../../../services/booking.service';
import { UserService } from '../../../services/user.service';
import { fieldRequired } from '../../../utils/form.utils';
import { getParticipantConditionLabel } from '../../../utils/seminar.utils';

type RevocationStep = 'form' | 'confirm';

const BOOKING_NUMBER_PREFIX = 'BCH-';

@Component({
  selector: 'app-contract-revocation-dialog',
  templateUrl: './contract-revocation-dialog.component.html',
  styleUrl: './contract-revocation-dialog.component.scss',
  standalone: false,
})
export class ContractRevocationDialogComponent implements OnDestroy {
  private destroy$ = new Subject<void>();
  public isLoading = false;
  public fieldRequired = fieldRequired;
  public getParticipantConditionLabel = getParticipantConditionLabel;
  public step: RevocationStep = 'form';
  public preview: ContractRevocationPreviewModel | null = null;
  public errorMessage = '';

  public revocationForm: FormGroup = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    bookingNumber: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
  });

  constructor(
    private bookingService: BookingService,
    private alertService: AlertService,
    private dialogRef: DynamicDialogRef,
    userService: UserService
  ) {
    const currentUser = userService.getUser();
    if (currentUser) {
      this.revocationForm.patchValue({
        firstName: currentUser.firstname,
        lastName: currentUser.lastname,
        email: currentUser.email,
        bookingNumber: BOOKING_NUMBER_PREFIX,
      });
    }

    this.setupBookingNumberNormalization();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public lookupBooking(): void {
    if (this.revocationForm.invalid) {
      this.revocationForm.markAllAsTouched();
      this.alertService.showErrorAlert('Bitte fülle alle Felder aus.');
      return;
    }

    this.errorMessage = '';
    this.isLoading = true;
    this.bookingService.getBookingForRevocation(this.getRequest()).subscribe({
      next: preview => {
        this.preview = preview;
        this.step = 'confirm';
        this.isLoading = false;
      },
      error: error => {
        this.errorMessage = this.getRevocationErrorMessage(error.message);
        this.isLoading = false;
      },
    });
  }

  public confirmRevocation(): void {
    this.errorMessage = '';
    this.isLoading = true;
    this.bookingService.revokeContract(this.getRequest()).subscribe({
      next: () => {
        this.alertService.showSuccessAlert(
          'Dein Vertragswiderruf wurde erfolgreich eingereicht.'
        );
        this.isLoading = false;
        this.dialogRef.close(true);
      },
      error: error => {
        this.errorMessage = this.getRevocationErrorMessage(error.message);
        this.isLoading = false;
      },
    });
  }

  public backToForm(): void {
    this.step = 'form';
    this.preview = null;
    this.errorMessage = '';
  }

  public closeDialog(): void {
    this.dialogRef.close();
  }

  public hasPriceDifference(preview: ContractRevocationPreviewModel): boolean {
    return Math.abs(preview.totalPrice - preview.discountedPrice) > 0.005;
  }

  public shouldShowPriceBreakdown(
    preview: ContractRevocationPreviewModel
  ): boolean {
    return (
      this.hasPriceDifference(preview) || (preview.coupons?.length ?? 0) > 0
    );
  }

  public getFinalPrice(preview: ContractRevocationPreviewModel): number {
    if (preview.discountedPrice < 0) {
      return 0;
    }
    return preview.discountedPrice;
  }

  public getCouponDiscountAmount(coupon: CouponCodeModel): number {
    if (!this.preview) {
      return 0;
    }
    if (coupon.discountType === 'percentage') {
      return this.preview.totalPrice * (coupon.value / 100);
    }
    return coupon.value;
  }

  private setupBookingNumberNormalization(): void {
    this.revocationForm
      .get('bookingNumber')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        const normalized = this.normalizeBookingNumber(value);
        if (normalized !== value) {
          this.revocationForm
            .get('bookingNumber')
            .setValue(normalized, { emitEvent: false });
        }
      });
  }

  private normalizeBookingNumber(value: string | null | undefined): string {
    const trimmed = (value ?? '').trim();
    const upper = trimmed.toUpperCase();

    if (upper === '' || BOOKING_NUMBER_PREFIX.startsWith(upper)) {
      return BOOKING_NUMBER_PREFIX;
    }

    let suffix: string;
    if (trimmed.includes('-')) {
      suffix = trimmed.split('-').pop()?.trim() ?? '';
    } else {
      suffix = trimmed.replace(/^BCH/i, '').trim();
    }

    if (suffix === '') {
      return BOOKING_NUMBER_PREFIX;
    }

    return BOOKING_NUMBER_PREFIX + suffix;
  }

  private getRequest(): ContractRevocationRequestModel {
    return {
      firstName: this.revocationForm.get('firstName').value,
      lastName: this.revocationForm.get('lastName').value,
      bookingNumber: this.normalizeBookingNumber(
        this.revocationForm.get('bookingNumber').value
      ),
      email: this.revocationForm.get('email').value,
    };
  }

  private getRevocationErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case 'booking_not_found':
        return 'Eine Buchung mit dieser Buchungsnummer konnte nicht gefunden werden. Bitte überprüfe deine Angaben.';
      case 'booking_already_cancelled':
        return 'Diese Buchung wurde bereits storniert.';
      case 'seminar_already_taken_place':
        return 'Ein bereits stattgefundenes Seminar kann nicht mehr widerrufen werden.';
      default:
        return 'Der Vertragswiderruf konnte leider nicht bearbeitet werden. Bitte versuche es später erneut.';
    }
  }
}
